
import React from 'react';
import { useParams } from 'react-router-dom';
import { TechniqueDetails } from '@/components/technique/TechniqueDetails';
import { useSavedTechniques } from '@/hooks/useSavedTechniques';
import { useSharedTechnique } from '@/hooks/useSharedTechnique';
import type { TechniquePosition, TechniqueType, TechniqueDifficulty } from '@/types/technique';

const SharedTechnique = () => {
  const { shareId } = useParams();

  // Use shared technique fetching
  const {
    sharedTechnique,
    loading
  } = useSharedTechnique(shareId);

  // Convert shared technique to regular technique format for compatibility
  const technique = sharedTechnique ? {
    id: sharedTechnique.technique_id || sharedTechnique.id,
    name: sharedTechnique.name || 'Untitled Technique',
    slug: sharedTechnique.share_id, // Use share_id as slug for shared techniques
    description: sharedTechnique.description || '',
    position_id: sharedTechnique.positionid || '',
    technique_position: (sharedTechnique.techniqueposition as TechniquePosition) || 'standing',
    technique_type: (sharedTechnique.techniquetype as TechniqueType) || 'other',
    difficulty: (sharedTechnique.difficulty as TechniqueDifficulty) || 'beginner',
    image_url: sharedTechnique.imageurl || undefined,
    video_url: sharedTechnique.videourl || undefined,
    key_points: sharedTechnique.keypoints || [],
    common_mistakes: sharedTechnique.commonmistakes || [],
    created_at: sharedTechnique.created_at || new Date().toISOString(),
    updated_at: new Date().toISOString(), // shared_techniques doesn't have updated_at
    is_user_created: false,
    created_by_user_id: sharedTechnique.creator_id || undefined,
    is_private: false
  } : null;

  // Use saved techniques hook for save functionality
  const techniques = technique ? [technique] : [];
  const { savedTechniques, handleSaveTechnique } = useSavedTechniques(techniques);

  if (!shareId) {
    return <div className="container mx-auto px-4 py-8 text-center">
      <p className="text-destructive">No share ID provided</p>
    </div>;
  }

  if (loading) {
    return <div className="container mx-auto px-4 py-8">Loading technique...</div>;
  }

  if (!sharedTechnique || !technique) {
    return <div className="container mx-auto px-4 py-8 text-center">
      <p>Technique not found</p>
    </div>;
  }

  const isSaved = savedTechniques.includes(technique.id);
  const onToggleSave = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleSaveTechnique(technique);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <TechniqueDetails 
        technique={technique} 
        isSaved={isSaved}
        onToggleSave={onToggleSave}
        isLoading={loading}
      />
    </div>
  );
};

export default SharedTechnique;
